import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { SITE } from '@/site';

const STORAGE_KEY = SITE.storageKeys.cookieConsent;

declare global {
  interface Window {
    __jscLoadTracking?: () => void;
  }
}

const copy = {
  ja: {
    title: 'クッキーの使用について',
    body: '同意すると、分析と広告のクッキーを使用します。クッキーを使わない基本的な訪問統計は常に取得します。同意しない場合も、ライブカメラや積雪情報はすべてご利用いただけます。',
    accept: '同意する',
    decline: '必要なものだけ',
    privacy: 'プライバシーポリシー',
  },
  en: {
    title: `Cookies on ${SITE.brand.name}`,
    body: 'Accept to allow analytics and advertising cookies. Basic visit statistics without cookies are always collected. Decline and everything — live cameras, snow data, forecasts — still works exactly the same.',
    accept: 'Accept',
    decline: 'Essential only',
    privacy: 'Privacy policy',
  },
  ko: {
    title: '쿠키 사용 안내',
    body: '동의하시면 분석·광고 쿠키를 사용합니다. 쿠키를 쓰지 않는 기본 방문 통계는 항상 수집됩니다. 거부하셔도 라이브 카메라와 적설 정보는 그대로 이용할 수 있습니다.',
    accept: '동의',
    decline: '필수만 사용',
    privacy: '개인정보 처리방침',
  },
  zh: {
    title: 'Cookie 使用说明',
    body: '同意后，我们会使用分析和广告 Cookie。不使用 Cookie 的基础访问统计始终会收集。即使拒绝，实时摄像头、积雪数据和天气预报也都可以照常使用。',
    accept: '同意',
    decline: '仅必要 Cookie',
    privacy: '隐私政策',
  },
} as const;

export function CookieConsent() {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Delay slightly so the banner never competes with first paint
        const timer = setTimeout(() => setVisible(true), 1200);
        return () => clearTimeout(timer);
      }
    } catch {
      /* storage blocked — do not nag */
    }
  }, []);

  const decide = (choice: 'accepted' | 'declined') => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      /* ignore */
    }
    if (choice === 'accepted') window.__jscLoadTracking?.();
    setVisible(false);
  };

  if (!visible) return null;

  const c = copy[language as keyof typeof copy] ?? copy.en;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={c.title}
      className="fixed inset-x-0 bottom-0 z-[60] p-2 sm:p-4 animate-in slide-in-from-bottom duration-300"
    >
      <div className="container max-w-4xl rounded-xl border border-border/60 bg-card/95 p-3 sm:p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <Cookie className="hidden h-5 w-5 shrink-0 text-primary sm:block" aria-hidden="true" />
          <div className="flex-1 text-xs leading-snug sm:text-sm sm:leading-normal">
            <p className="font-display text-sm font-semibold text-foreground">{c.title}</p>
            <p className="mt-0.5 text-muted-foreground sm:mt-1">
              {c.body}{' '}
              <Link
                to={`/${language}/privacy/`}
                className="underline hover:text-foreground"
              >
                {c.privacy}
              </Link>
            </p>
          </div>
          <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex">
            <Button variant="outline" size="sm" onClick={() => decide('declined')}>
              {c.decline}
            </Button>
            <Button size="sm" onClick={() => decide('accepted')}>
              {c.accept}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
